/** QuickRouter — Police vectorielle à traits (sérigraphie Gerber et aperçus SVG).
 *
 * Dessin maison inspiré du Hershey simplex : chaque glyphe tient dans une grille de
 * 4 × 6 unités (6 = hauteur de capitale), en traits polylignes « x,y x,y … ». Les
 * minuscules reprennent les capitales réduites (lisibles à 1 mm). Les accents français
 * sont ramenés à la lettre de base. Y vers le haut.
 */

const O = '1,0 0,1 0,5 1,6 3,6 4,5 4,1 3,0 1,0';
const P = '0,0 0,6 3,6 4,5 4,4 3,3 0,3';

const GLYPHS = {
  A: ['0,0 0,4 2,6 4,4 4,0', '0,3 4,3'],
  B: ['0,0 0,6 3,6 4,5 4,4 3,3 0,3', '3,3 4,2 4,1 3,0 0,0'],
  C: ['4,5 3,6 1,6 0,5 0,1 1,0 3,0 4,1'],
  D: ['0,0 0,6 2,6 4,4 4,2 2,0 0,0'],
  E: ['4,6 0,6 0,0 4,0', '0,3 3,3'],
  F: ['4,6 0,6 0,0', '0,3 3,3'],
  G: ['4,5 3,6 1,6 0,5 0,1 1,0 3,0 4,1 4,3 2,3'],
  H: ['0,0 0,6', '4,0 4,6', '0,3 4,3'],
  I: ['1,0 3,0', '1,6 3,6', '2,0 2,6'],
  J: ['4,6 4,1 3,0 1,0 0,1'],
  K: ['0,0 0,6', '4,6 0,2', '1,3 4,0'],
  L: ['0,6 0,0 4,0'],
  M: ['0,0 0,6 2,3 4,6 4,0'],
  N: ['0,0 0,6 4,0 4,6'],
  O: [O],
  P: [P],
  Q: [O, '2,2 4,0'],
  R: [P, '2,3 4,0'],
  S: ['4,5 3,6 1,6 0,5 0,4 1,3 3,3 4,2 4,1 3,0 1,0 0,1'],
  T: ['0,6 4,6', '2,6 2,0'],
  U: ['0,6 0,1 1,0 3,0 4,1 4,6'],
  V: ['0,6 2,0 4,6'],
  W: ['0,6 1,0 2,3 3,0 4,6'],
  X: ['0,0 4,6', '0,6 4,0'],
  Y: ['0,6 2,3 4,6', '2,3 2,0'],
  Z: ['0,6 4,6 0,0 4,0'],
  0: [O, '1,1 3,5'],
  1: ['1,5 2,6 2,0', '1,0 3,0'],
  2: ['0,5 1,6 3,6 4,5 4,4 0,0 4,0'],
  3: ['0,5 1,6 3,6 4,5 4,4 3,3 4,2 4,1 3,0 1,0 0,1', '1,3 3,3'],
  4: ['3,0 3,6 0,2 4,2'],
  5: ['4,6 0,6 0,3 3,3 4,2 4,1 3,0 1,0 0,1'],
  6: ['4,5 3,6 1,6 0,5 0,1 1,0 3,0 4,1 4,2 3,3 0,3'],
  7: ['0,6 4,6 1,0'],
  8: ['1,3 0,4 0,5 1,6 3,6 4,5 4,4 3,3 1,3 0,2 0,1 1,0 3,0 4,1 4,2 3,3'],
  9: ['0,1 1,0 3,0 4,1 4,5 3,6 1,6 0,5 0,4 1,3 4,3'],
  '-': ['1,3 3,3'],
  '+': ['0,3 4,3', '2,1 2,5'],
  '.': ['2,0 2,0.4'],
  ',': ['2,0.5 2,0 1.5,-1'],
  ':': ['2,1 2,1.4', '2,4 2,4.4'],
  '/': ['0,0 4,6'],
  _: ['0,-0.5 4,-0.5'],
  '(': ['3,6 2,5 1.5,3 2,1 3,0'],
  ')': ['1,6 2,5 2.5,3 2,1 1,0'],
  '#': ['1,0 1.5,6', '2.5,0 3,6', '0,2 4,2', '0,4 4,4'],
  '%': ['0,0 4,6', '0.5,5.5 1.2,5.5 1.2,4.8 0.5,4.8 0.5,5.5', '2.8,1.2 3.5,1.2 3.5,0.5 2.8,0.5 2.8,1.2'],
  'µ': ['0,-1.5 0,4', '0,1 1,0 3,0 4,1', '4,4 4,0'],
  'Ω': ['0,0 1.5,0 1.5,1 0,2.5 0,4.5 1,6 3,6 4,4.5 4,2.5 2.5,1 2.5,0 4,0'],
  '~': ['0,3 1,4 3,2 4,3'],
  '=': ['0,2 4,2', '0,4 4,4'],
  '<': ['4,5 0,3 4,1'],
  '>': ['0,5 4,3 0,1'],
  "'": ['2,6 2,4.5'],
  '"': ['1.5,6 1.5,4.5', '2.5,6 2.5,4.5'],
  '?': ['0,5 1,6 3,6 4,5 4,4 2,3 2,2', '2,0 2,0.4'],
  ' ': [],
};

const PARSED = Object.fromEntries(Object.entries(GLYPHS).map(([k, strokes]) => [
  k,
  strokes.map((s) => s.split(' ').map((p) => p.split(',').map(Number))),
]));

const ADVANCE = 6; // largeur 4 + interlettre 2 (unités)
const LOWER = { sx: 0.8, sy: 0.72 };

/** Épaisseur de trait conseillée pour une hauteur de capitale donnée. */
export const strokeWidth = (size) => Math.round(size * 0.15 * 1000) / 1000;

/** Glyphe d'un caractère : { strokes, sx, sy } (accents ramenés à la lettre de base). */
function glyph(ch) {
  const base = ch.normalize('NFD').replace(/[̀-ͯ]/g, '');
  if (PARSED[base]) return { strokes: PARSED[base], sx: 1, sy: 1 };
  const up = base.toUpperCase();
  if (up !== base && PARSED[up]) return { strokes: PARSED[up], ...LOWER };
  if (ch === 'Ω' || ch === 'µ') return { strokes: PARSED[ch], sx: 1, sy: 1 };
  if (ch === 'μ') return { strokes: PARSED['µ'], sx: 1, sy: 1 }; // mu grec
  if (ch === 'œ' || ch === 'Œ') return glyph('O');
  return { strokes: PARSED['?'], sx: 1, sy: 1 };
}

/**
 * Texte → polylignes en mm. pos = point d'ancrage (centré verticalement sur la hauteur
 * de capitale ; horizontalement selon align), size = hauteur de capitale, rot en degrés,
 * mirror = miroir horizontal (sérigraphie de la face dessous, lue par transparence).
 */
export function strokeText(text, { pos = [0, 0], size = 1, rot = 0, align = 'center', mirror = false } = {}) {
  const u = size / 6;
  const local = [];
  let x = 0;
  for (const ch of String(text)) {
    const g = glyph(ch);
    for (const s of g.strokes) local.push(s.map(([gx, gy]) => [x + gx * g.sx * u, gy * g.sy * u]));
    x += ADVANCE * g.sx * u;
  }
  const width = Math.max(0, x - 2 * u); // sans l'interlettre finale
  const dx = align === 'left' ? 0 : align === 'right' ? -width : -width / 2;
  const a = (rot * Math.PI) / 180;
  const c = Math.cos(a), s = Math.sin(a);
  const lines = local.map((pl) => pl.map(([lx, ly]) => {
    let px = lx + dx;
    const py = ly - size / 2;
    if (mirror) px = -px;
    return [pos[0] + px * c - py * s, pos[1] + px * s + py * c];
  }));
  return { lines, width };
}
